import { useState, useRef, useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Add, Minus, DocumentDownload } from "iconsax-react";
import Loader from "@/shared/components/Loader";
import ModalWrapper from "../ModalWrapper";
import Button from "../Button";

interface Props {
  imageUrl: string;
  fileName?: string;
  onClose: () => void;
}

export default function ImageViewer({ imageUrl, fileName, onClose }: Props) {
  const { t } = useTranslation("Common");
  const [scale, setScale] = useState(1.0);
  const [isLoaded, setIsLoaded] = useState(false);
  const [error, setError] = useState(false);
  const [baseSize, setBaseSize] = useState<{ width: number; height: number } | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);

  const displayName = fileName || decodeURIComponent(imageUrl.split("/").pop()?.split("?")[0] ?? "image");

  useEffect(function computeFitSize() {
    if (!isLoaded || error || baseSize) return;
    const img = imgRef.current;
    const container = containerRef.current;
    if (!img || !container) return;

    const { naturalWidth, naturalHeight } = img;
    const containerH = container.clientHeight;
    const ratio = containerH / naturalHeight;

    setBaseSize({
      width: Math.floor(naturalWidth * ratio),
      height: Math.floor(naturalHeight * ratio),
    });
  }, [isLoaded, error, baseSize]);

  function handleZoomIn() {
    setScale((prev) => Math.min(prev + 0.25, 5));
  }

  function handleZoomOut() {
    setScale((prev) => Math.max(prev - 0.25, 0.5));
  }

  function handleDownload() {
    const link = document.createElement("a");
    link.href = imageUrl;
    link.download = displayName;
    link.target = "_blank";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  const handleWheel = useCallback((e: React.WheelEvent) => {
    if (!e.ctrlKey) return;
    e.preventDefault();
    if (e.deltaY < 0) {
      setScale((prev) => Math.min(prev + 0.1, 5));
    } else {
      setScale((prev) => Math.max(prev - 0.1, 0.5));
    }
  }, []);

  return (
    <ModalWrapper onClose={onClose} width="w-7xl h-[80vh]">
      <p className="text-heading-sm content-base-primary truncate pr-8 shrink-0">{displayName}</p>

      <div className={`flex items-center justify-end shrink-0${!isLoaded || error ? " invisible" : ""}`}>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={handleZoomOut}
            disabled={scale <= 0.5}
            className="w-8 h-8 flex items-center justify-center rounded-lg cursor-pointer content-base-secondary hover:surface-tertiary-fill disabled:opacity-40 disabled:cursor-not-allowed">
            <Minus size={16} color="currentColor" />
          </button>
          <span className="text-label-sm content-base-secondary px-2">{Math.round(scale * 100)}%</span>
          <button
            type="button"
            onClick={handleZoomIn}
            disabled={scale >= 5}
            className="w-8 h-8 flex items-center justify-center rounded-lg cursor-pointer content-base-secondary hover:surface-tertiary-fill disabled:opacity-40 disabled:cursor-not-allowed">
            <Add size={16} color="currentColor" />
          </button>
          <Button size="md" onClick={handleDownload}>
            <DocumentDownload size={16} color="currentColor" />
            {t("pdfViewer.download")}
          </Button>
        </div>
      </div>

      <div
        ref={containerRef}
        onWheel={handleWheel}
        className="component-scroll flex items-center justify-center flex-1 min-h-0 rounded-2xl surface-component-fill overflow-auto">
        {error ? (
          <div className="flex items-center justify-center py-20">
            <p className="text-body-regular-md content-base-secondary">{t("pdfViewer.loadError")}</p>
          </div>
        ) : (
          <>
            {!isLoaded && (
              <div className="flex items-center justify-center py-20 text-brand-500">
                <Loader />
              </div>
            )}
            <img
              ref={imgRef}
              src={imageUrl}
              alt={displayName}
              className="max-w-none transition-[width,height] duration-150"
              style={{
                width: baseSize ? baseSize.width * scale : undefined,
                height: baseSize ? baseSize.height * scale : undefined,
                display: baseSize ? "block" : "none",
              }}
              onLoad={() => setIsLoaded(true)}
              onError={() => setError(true)}
            />
          </>
        )}
      </div>
    </ModalWrapper>
  );
}
